import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  MessageSquare,
  ClipboardList,
  Clock,
  Activity,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchWithAuth } from "@/lib/api/client";

interface ActivityItem {
  type: string;
  title: string;
  description?: string;
  entity_type: string;
  entity_id: string;
  created_at: string;
  user_name?: string;
}

interface ActivityListResponse {
  data: ActivityItem[];
  total: number;
  limit: number;
  offset: number;
}

const ICON_MAP: Record<string, React.ElementType> = {
  document_uploaded: FileText,
  licitacion_created: ClipboardList,
  proposal_created: MessageSquare,
};

// Sistema de diseño Meridianos: variantes de color por tipo de actividad
// usando los acentos secundarios del DS (rose/sage/slate-brand) sobre la
// paleta neutra. El primario teal queda reservado para CTA.
const ICON_COLOR_MAP: Record<string, string> = {
  document_uploaded:  "text-slateBrand bg-slateBrand/12",
  licitacion_created: "text-sage-dark bg-sage/15",
  proposal_created:   "text-rose-dark bg-rose/15",
};

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "Hace un momento";
  }
  if (diffMinutes < 60) {
    return `Hace ${diffMinutes} min`;
  }
  if (diffHours < 24) {
    return `Hace ${diffHours}h`;
  }
  if (diffDays < 7) {
    return `Hace ${diffDays}d`;
  }
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
  });
}

export function RecentActivity() {
  const {
    data: activities,
    isLoading,
    isError,
  } = useQuery<ActivityItem[]>({
    queryKey: ["recent-activity"],
    queryFn: async () => {
      try {
        const res: ActivityListResponse = await fetchWithAuth("/api/v1/activity?limit=10");
        return Array.isArray(res) ? res : (res.data ?? []);
      } catch {
        return [];
      }
    },
    retry: false,
    staleTime: 2 * 60 * 1000,
  });

  // El componente renderiza SOLO la lista (sin Card wrapper) — el padre
  // que lo monta (Dashboard) ya provee el contenedor visual con el
  // estilo del DS Meridianos.
  if (isLoading) return <ActivitySkeleton />;
  if (isError || !activities || activities.length === 0) {
    return <EmptyActivity />;
  }
  return (
    <ul className="divide-y divide-line">
      {activities.slice(0, 6).map((item, idx) => (
        <ActivityRow
          key={`${item.entity_type}-${item.entity_id}-${idx}`}
          item={item}
        />
      ))}
    </ul>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const Icon = ICON_MAP[item.type] ?? FileText;
  const colorClasses =
    ICON_COLOR_MAP[item.type] ?? "text-content-muted bg-surface-200";

  return (
    <li className="flex items-start gap-3 px-5 py-3 hover:bg-surface-100 transition-colors">
      <div className={`flex-shrink-0 rounded p-1.5 ${colorClasses}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-content">{item.title}</p>
        {item.description && (
          <p className="text-xs text-content-muted truncate">
            {item.description}
          </p>
        )}
      </div>
      <span className="flex-shrink-0 flex items-center gap-1 text-[11px] text-content-muted whitespace-nowrap">
        <Clock className="h-3 w-3" />
        {formatRelativeTime(item.created_at)}
      </span>
    </li>
  );
}

function EmptyActivity() {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center px-4">
      <Activity className="h-8 w-8 text-line-faint" />
      <p className="mt-3 text-sm font-heading font-semibold text-content">
        Aún no hay actividad reciente
      </p>
      <p className="mt-1 text-xs text-content-muted">
        Las acciones del equipo aparecerán aquí.
      </p>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-5 py-3">
          <Skeleton className="h-7 w-7 rounded" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
          <Skeleton className="h-2.5 w-12" />
        </li>
      ))}
    </ul>
  );
}
